import Card from "@/design-system/Card/Card";
import { StatChip } from "@/design-system/StatChip/StatChip";
import { CoinIcon } from "@/designSystemIcons";
import { expire as expireIcon, repeatable } from "@/icons";
import { getSourceDisplay } from "@/pages/EvolutionsHub/EvolutionsBuilder/components/evoDisplayUtils";
import {
  evoStateItems,
  getEvoStateIcon,
} from "@/pages/EvolutionsHub/EvolutionsOverviewPage/EvolutionsOverviewPage";
import {
  Evolution,
  EvolutionCategory,
} from "@/redux/evolutions/evolutionsSlice";
import { eventTokensSelector } from "@/redux/loadedContent/loadedContentSlice";
import { BREAKPOINTS } from "@/settings";
import { formatEvoCost } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { formatTime } from "@repo/shared/utils";
import { Dna, Lock } from "lucide-react";
import { MouseEvent, ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

const cdnBase = import.meta.env.VITE_APP_ASSETS_BASE_URL;

/**
 * An evolution's key facts: state, category, price, duration, lock, matches,
 * repeats, source. Tiles that wrap on wide screens, a list in a flat card on
 * phones. Same icons the overview cards use.
 */
export const EvoFacts = ({
  evolution,
  category,
  onChangeState,
}: {
  evolution: Evolution;
  category?: EvolutionCategory;
  /** Makes the state chip pressable. Leave out where state can't be changed. */
  onChangeState?: (e: MouseEvent<HTMLElement>) => void;
}) => {
  const { t } = useTranslation("evolutions");
  const { t: tCommon } = useTranslation("common");
  // Tiles wrap into a ragged grid on phones; a list reads better there.
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const variant = isMobile ? "row" : "tile";
  const stateItem = evoStateItems.find(
    (item) => item.state === evolution.state,
  );
  const eventTokens = useSelector(eventTokensSelector);
  const { sourceIcon, sourceLabel, sourceValue } = useMemo(
    () => getSourceDisplay(evolution.source, evolution.state, t, eventTokens),
    [evolution.source, evolution.state, t, eventTokens],
  );

  const duration = useMemo(
    () =>
      evolution.completeBy
        ? formatTime(evolution.completeBy * 1000 - Date.now())
        : undefined,
    [evolution.completeBy],
  );
  const lock = useMemo(
    () =>
      evolution.unlockBy
        ? formatTime(evolution.unlockBy * 1000 - Date.now())
        : undefined,
    [evolution.unlockBy],
  );

  const coins = evolution.cost?.coins ?? 0;
  const points = evolution.cost?.points ?? 0;
  const price: ReactNode =
    coins > 0 ? formatEvoCost(coins) : points > 0 ? points : tCommon("free");

  return (
    <Facts isMobile={isMobile}>
      <StatChip
        variant={variant}
        label={t("info.state")}
        value={stateItem ? t(stateItem.labelKey) : ""}
        icon={getEvoStateIcon(evolution.state, 16)}
        onClick={onChangeState}
      />
      {category && (
        <StatChip
          variant={variant}
          label={t("info.category")}
          value={category.name}
          icon={<Dna size={16} />}
        />
      )}
      {evolution.source?.type !== "token-store" && (
        <StatChip
          variant={variant}
          label={t("header.price")}
          value={price}
          icon={
            points > 0 && coins === 0 ? (
              <img
                alt={tCommon("alt.points")}
                className="size-4"
                src={`${cdnBase}/fc24/misc/points.png`}
              />
            ) : (
              <CoinIcon size={16} />
            )
          }
          valueClassName="text-evolution-secondary"
        />
      )}
      {lock && (
        <StatChip
          variant={variant}
          label={t("header.unlockBy")}
          value={lock}
          icon={<Lock size={16} />}
        />
      )}
      {duration && (
        <StatChip
          variant={variant}
          label={t("header.completeBy")}
          value={duration}
          icon={<div className="size-4 fill-current">{expireIcon}</div>}
        />
      )}
      <StatChip
        variant={variant}
        label={t("info.matches")}
        value={evolution.matches ?? "?"}
        icon={
          <img
            src={`${cdnBase}/misc/ea/ball.png`}
            alt={t("alt.matches")}
            className="size-4"
          />
        }
      />
      {evolution.repeats > 0 && (
        <StatChip
          variant={variant}
          label={t("info.repeats")}
          value={evolution.repeats}
          icon={<div className="size-4">{repeatable}</div>}
        />
      )}
      {sourceLabel && (
        <StatChip
          variant={variant}
          label={sourceLabel}
          value={sourceValue ?? ""}
          icon={sourceIcon}
        />
      )}
    </Facts>
  );
};

/** Tiles wrap freely; rows sit in one flat card. */
const Facts = ({
  isMobile,
  children,
}: {
  isMobile: boolean;
  children: ReactNode;
}) =>
  isMobile ? (
    <Card hideBorder hoverable={false} className="px-4 py-2">
      {children}
    </Card>
  ) : (
    <div className="flex flex-wrap gap-3">{children}</div>
  );
